import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'
import { SocketEvent } from '~/constants/socket'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { ProgressMessageType } from '~/protos/agents'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { AppEvents, globalEventEmitter } from '~/utils/shared'

/**
 * Structured detail with explicit hierarchy level
 */

/**
 * Progress message as returned from the API
 */
/** Todo item in a progress message checklist */
export interface ProgressMessageTodoItem {
  todo: string
  status: 'TODO_PENDING' | 'TODO_IN_PROGRESS' | 'TODO_COMPLETED'
}

export interface ProgressMessage {
  taskStatus: string | null
  messageType: ProgressMessageType
  title: string
  internalMsgs: string[]
  toolOutputs: string[]
  checkpointMsg: string
  msgHash: string
  createdTsMs: number // Milliseconds since epoch (converted from nanoseconds by API)
  todoList?: ProgressMessageTodoItem[] // For TODO_LIST message type
}

interface ProgressMessagesResponse {
  progressMessages: ProgressMessage[]
}

const fetchTaskProgressMessages = async (
  workspaceUuid: string,
  conversationUuid: string,
  taskId: string
): Promise<ProgressMessage[]> => {
  const response = await fetchWrapper(
    `/api/workspace/${workspaceUuid}/conversation/${conversationUuid}/task/${taskId}/progress`,
    {
      method: 'GET'
    }
  )

  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to fetch task progress messages: ${reason}`)
  }

  const data: ProgressMessagesResponse = await response.json()
  return data.progressMessages
}

/**
 * Hook to fetch progress messages for a task.
 * Uses WebSocket events for real-time updates instead of polling.
 *
 * @param conversationUuid - The conversation UUID
 * @param taskId - The task ID
 * @param options - Optional configuration
 * @param options.enabled - Whether the query should run (default: true when all params present)
 * @param options.refetchInterval - Optional polling fallback, used by long-running task panels.
 */
export const useTaskProgressMessages = (
  conversationUuid: string,
  taskId: string,
  options?: {
    enabled?: boolean
    refetchInterval?: number | false
  }
) => {
  const { currentWorkspace } = useWorkspaceContext()
  const workspaceUuid = currentWorkspace?.uuid || ''
  const queryClient = useQueryClient()

  const isEnabled =
    options?.enabled !== undefined
      ? options.enabled
      : !!workspaceUuid && !!conversationUuid && !!taskId

  // Throttled invalidation — coalesces rapid-fire TaskProgressUpdated events
  // so we refetch at most once per 2 seconds instead of on every WAL event.
  // Uses leading-edge throttle: first event fires, subsequent ones within the
  // window are dropped (not deferred).
  const invalidateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const throttledInvalidate = useCallback(() => {
    if (invalidateTimerRef.current) return
    invalidateTimerRef.current = setTimeout(() => {
      invalidateTimerRef.current = null
      queryClient.invalidateQueries({
        queryKey: ['TaskProgressMessages', workspaceUuid, conversationUuid, taskId]
      })
    }, 2000)
  }, [queryClient, workspaceUuid, conversationUuid, taskId])

  useEffect(() => {
    return () => {
      if (invalidateTimerRef.current) clearTimeout(invalidateTimerRef.current)
    }
  }, [])

  // Listen for WebSocket progress updates
  useEffect(() => {
    if (!isEnabled || !taskId) return

    const handleProgressUpdate = (...args: unknown[]) => {
      const data = args[0] as {
        event: string
        data: {
          task_id?: number
          conversation_uuid?: string
        }
      }

      if (data.event !== SocketEvent.TaskProgressUpdated) return
      if (String(data.data?.task_id) !== taskId) return

      throttledInvalidate()
    }

    globalEventEmitter.on(AppEvents.SocketEvent, handleProgressUpdate)
    return () => {
      globalEventEmitter.off(AppEvents.SocketEvent, handleProgressUpdate)
    }
  }, [isEnabled, taskId, conversationUuid, queryClient, throttledInvalidate])

  return useQuery({
    queryKey: ['TaskProgressMessages', workspaceUuid, conversationUuid, taskId],
    queryFn: () => fetchTaskProgressMessages(workspaceUuid, conversationUuid, taskId),
    enabled: isEnabled,
    staleTime: 1000, // Consider data stale after 1s
    refetchInterval: options?.refetchInterval
  })
}
